
import { useEffect, useState } from "react"
import { apiFetch } from '@/lib/api'
import { FiEdit, FiPlus, FiTrash, FiUpload, FiEdit2, FiDownload } from "react-icons/fi"
import { MdOutlineVideoCameraBack, MdOutlineImage, MdCheckBox } from "react-icons/md";
import { PiFileDashedLight } from "react-icons/pi";

import { useModal } from "@/contexts/ModalContext"
import { FaPlay } from "react-icons/fa"
import VideoPreviewModal from "./modals/VideoPreviewModal"
import Button from "./ui/Button"
import AssetsEditorModal from "./modals/AssetsEditorModal"
import ImagePreviewModal from "./modals/ImagePreviewModal"
import { UserRole } from "@/contexts/UserContext";
import AudioPreviewModal from "./modals/AudioPreviewModal"
import { LuAudioLines } from "react-icons/lu"
import { useShow } from "@/contexts/ShowContext"
import AssetsBrowserModal from "./modals/AssetsBrowserModal"
import ConfirmModal from "./modals/ConfirmModal"
import AssetEditorModal from "./modals/AssetEditorModal"
import useFileUpload from "@/hooks/useFileUpload"
import Loader from "./loader"
import AssetThumbnail from "./AssetThumbnail";
import { useEpisode } from "@/contexts/EpisodeContext";

const RundownAsset = ({asset, onPreview, onDelete, onEdit}) =>{

    const getName = () =>{
        const maxChars = 30
        if(asset.type==='script')return asset.text?.length > maxChars ? asset.text.slice(0,maxChars) + '...' : asset.text
        else return asset.name?.length > maxChars ? '...' + asset.name.slice(-maxChars) : asset.name
    }

    return(
        <div className='cursor-pointer flex flex-col group/asset'>
            <div className="w-full aspect-video relative flex-none rounded-md overflow-hidden" onClick={() => onPreview(asset)}>
                <AssetThumbnail asset={asset} />
                {/* <div className="absolute top-0 left-0 w-full h-full bg-background/50 hidden group-hover/asset:flex items-center justify-center" onClick={() => onPreview(asset)}>
                    <FaPlay className="w-5 h-5 text-white" />
                </div> */}
                {/* <div className="flex items-center justify-end gap-1 absolute top-1 right-1 px-1 py-[2px] rounded-sm bg-card">
                    {asset.status === 'approved' && <MdCheckBox className="w-3 h-3 text-green-500" />}
                    {asset.status === 'draft' && <PiFileDashedLight className="w-3 h-3 text-gray-400" />}
                    <span className='text-[10px] text-secondary-foreground capitalize'>{asset.status}</span>
                </div> */}
                {/* <div className="absolute right-1 bottom-1 items-center gap-1 hidden group-hover/asset:flex">
                    <Button type='secondary' icon={<FiEdit className="w-3 h-3 text-secondary-foreground" />} onClick={() => onEdit(asset)} />
                    <Button type='danger' icon={<FiTrash className="w-3 h-3 text-secondary-foreground" />} onClick={() => onDelete(asset)} />
                </div> */}

                <div className='absolute bottom-0 right-0 p-1 hidden group-hover/asset:flex'>
                    <div className='flex items-center gap-2 bg-card rounded-full py-1 px-2 border'>
                        <FiEdit2 className="w-3 h-3 text-secondary-foreground/70 hover:text-secondary-foreground cursor-pointer" onClick={(e) => {e.stopPropagation(); onEdit(asset)}} />
                        <FiTrash className="w-3 h-3 text-destructive-foreground/70 hover:text-destructive-foreground cursor-pointer" onClick={(e) => {e.stopPropagation(); onDelete(asset)}}  />
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-2 w-full p-1">
                <span className='text-xs text-secondary-foreground flex-1'>
                    {getName()}
                </span>
            </div>
            
        </div>
    )
}

export default function Scene({ data, onChange }) {
    const [scene, setScene] = useState(data)
    const { openModal } = useModal();
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [updating, setUpdating] = useState(false)
    const {show} = useShow()
    const [assets, setAssets] = useState([])
    const [loadingStatus, setLoadingStatus] = useState('')
    const {uploadFile, uploadProgress, isUploading} = useFileUpload()
    const {updateScene} = useEpisode()

    const fetchScene = async () => {
        try{
            const response = await apiFetch(`/api/shows/${scene.show}/episodes/${scene.episode}/segments/${scene.segment}/scenes/${scene._id}`)
            if(!response.ok){
                throw new Error('Failed to fetch scene')
            }
            const sceneData = await response.json()
            setScene(sceneData)
            if(onChange) onChange(sceneData)
        } catch (error) {
            console.error('Error fetching scene:', error)
        }
    }

    const handleEditAsset = async (asset, tag) => {
        let assetData;

        assetData = await openModal(<AssetEditorModal asset={asset} allowTags={tag ? false : true} />, 'side')

        if(!assetData) return

        try{
            setUpdating(true)
            setLoadingStatus('Saving: ' + assetData.name)
            let uploadResponse = {}

            if(assetData.file){
                setLoadingStatus(`Uploading: ${assetData.file.name}`)
                uploadResponse = await uploadFile(assetData.file)
                assetData.mediaType = assetData.file.type.split('/')[0]
                delete assetData.file
            }
            
            setLoadingStatus(`Saving: ${assetData.name}`)

            const assetResponse = await apiFetch(asset?._id ? `/api/shows/${show._id}/assets/${asset._id}` : `/api/shows/${show._id}/assets`, {
                method: asset?._id ? 'PUT' : 'POST',
                body: JSON.stringify({
                    ...assetData,
                    ...uploadResponse,
                    tags : tag ? [tag._id] : (assetData?.tags || []),
                    source : 'upload'
                })
            })

            if (!assetResponse.ok) throw new Error('Failed to add asset')

            if(!asset?._id){
                const newAsset = await assetResponse.json()
                await updateScene(scene, {assets: [...scene.assets, newAsset]}, false)
            }

            await fetchScene()

        } catch (error) {
            console.error('Error in file select:', error)
        } finally{
            setUpdating(false)
            setLoadingStatus('')
        }
    }

    const handleAddAssets = async (tag) => {
        const asset = await openModal(<AssetsBrowserModal tags={tag ? [tag._id] : []} header={`Add ${tag?.name || 'Untagged Asset'}`} showUntagged={tag ? false : true} />)
        if(!asset) return

        try{
            setUpdating(true)
            if(!scene.assets.some(a => a._id === asset._id)){
                await updateScene(scene, {assets: [...scene.assets, asset]}, false)
                await fetchScene()
            }
        } catch (error) {
            console.error('Error in import:', error)
        } finally {
            setUpdating(false)
        }
    }

    const handleDeleteAsset = async (asset) => {
        const confirm = await openModal(<ConfirmModal title='Remove Asset' message={`Are you sure you want to remove ${asset.name} from this scene?`} />)
        if(!confirm) return
        try{
            setUpdating(true)
            await updateScene(scene, {assets: scene.assets.filter(a => a._id !== asset._id)}, false)
            await fetchScene()
        }
        catch(error){
            console.error('Error in delete:', error)
        } finally {
            setUpdating(false)
        }
    }
    
    const handleAssetsPreview = (asset) => {
        if(asset.mediaType === 'video'){
            openModal(<VideoPreviewModal video={asset} />)
        } else if(asset.mediaType === 'audio'){
            openModal(<AudioPreviewModal audio={asset} />)
        } else if(asset.mediaType==='image') {
            openModal(<ImagePreviewModal image={asset} />)
        }
    }

    return (
        <div className='flex flex-col relative flex-none border-b group/scene'>
            <div className='flex relative w-full'>
                <div className="relative group flex-none py-4 flex flex-col gap-5 w-[400px] border-r">
                    <div className='flex flex-col gap-1 px-4'>
                        {scene.name && <h1 className='text-md text-primary'>{scene.name}</h1>}
                        {scene.description && <h2 className='text-sm text-secondary-foreground/60'>{scene.description}</h2>}
                    </div>
                    {scene.script.map((element, index) => (
                        <div key={index} className='flex gap-4 px-4 items-start text-secondary-foreground text-sm'>
                            {element.character?.thumbnail_url && 
                                <img src={element.character?.thumbnail_url} alt={element.character?.name} loading="lazy" decoding="async" width={44} height={44} className="flex-none object-cover w-11 h-11 relative overflow-hidden rounded-full" />
                            }
                            <div className='flex flex-col gap-1'>
                                {element.description && <span className='text-xs/5 italic text-secondary-foreground/60'>{element.description}</span>}
                                {element.speech && <span className='text-sm/6 text-white'>{element.speech}</span>}
                            </div>
                        </div>
                    ))}
                </div>

                {show.asset_categories.map(tag => (
                    <div key={tag._id} className="relative px-2 py-4 flex-none flex flex-col gap-2 group w-60 border-r">
                        <div className='flex gap-2 flex-1 flex-col'>
                            {scene.assets.filter(asset => asset.tags.includes(tag._id)).map(asset => 
                                <RundownAsset key={asset._id} asset={asset} onPreview={handleAssetsPreview} onDelete={handleDeleteAsset} onEdit={handleEditAsset} />
                            )}
                            <div className='flex flex-col gap-2 opacity-0 group-hover:opacity-100'>
                                <div className='flex items-center justify-center w-full p-4 border border-dashed border-secondary-foreground/50 rounded-md gap-2 cursor-pointer opacity-80 hover:opacity-100 hover:bg-card' onClick={() => handleAddAssets(tag)}>
                                    <FiPlus className='w-4 h-4 text-secondary-foreground' />
                                    <span className='text-secondary-foreground text-xs select-none capitalize'>Add {tag.name}</span>
                                </div>
                                <div className='flex items-center justify-center w-full p-4 border border-dashed border-secondary-foreground/50 rounded-md gap-2 cursor-pointer opacity-80 hover:opacity-100 hover:bg-card' onClick={() => handleEditAsset(null, tag)}>
                                    <FiUpload className='w-4 h-4 text-secondary-foreground' />
                                    <span className='text-secondary-foreground text-xs select-none capitalize'>Upload {tag.name}</span>
                                </div>
                            </div>
                            
                        </div>
                    </div>
                ))}  

                <div className="relative group px-2 py-4 flex-none flex flex-col gap-2 group w-60">
                    <div className='flex gap-2 flex-1 flex-col'>
                        {scene.assets.filter(asset => !asset.tags?.length).map(asset => 
                            <RundownAsset key={asset._id} asset={asset} onPreview={handleAssetsPreview} onDelete={handleDeleteAsset} onEdit={handleEditAsset} />
                        )}
                        <div className='flex flex-col gap-2 opacity-0 group-hover:opacity-100'>
                            <div className='flex items-center justify-center w-full p-4 border border-dashed border-secondary-foreground/50 rounded-md gap-2 cursor-pointer opacity-80 hover:opacity-100 hover:bg-card' onClick={() => handleAddAssets()}>
                                <FiPlus className='w-4 h-4 text-secondary-foreground' />
                                <span className='text-secondary-foreground text-xs select-none capitalize'>Add Asset</span>
                            </div>
                            <div className='flex items-center justify-center w-full p-4 border border-dashed border-secondary-foreground/50 rounded-md gap-2 cursor-pointer opacity-80 hover:opacity-100 hover:bg-card' onClick={() => handleEditAsset()}>
                                <FiUpload className='w-4 h-4 text-secondary-foreground' />
                                <span className='text-secondary-foreground text-xs select-none capitalize'>Upload Asset</span>
                            </div>
                        </div>
                    </div>
                </div>

                {updating && <Loader status={loadingStatus} showProgress={isUploading} progress={uploadProgress} />}
            </div>
        </div>
    )
}